/*
  This is a middleware that every API call goes through. It's a useful layer for efficiently adding things shared between every API function, 
  like error handling and token refreshing.
*/

import axios from "axios"
import jwtDecode from "jwt-decode"
import dayjs from "dayjs"
import constants from "../../Constants"
import types from "../Actions/types"

const host = constants.HOST

const apiCallMap = {
  GET: axios.get,
  POST: axios.post,
  DELETE: axios.delete,
  PUT: axios.put
}

const statusesToRedirectToLogin = [419, 406, 401, 403]

const BEARER_PREFIX_RE = /^Bearer\s+/i
const REFRESH_WAIT_MS = 50
const REFRESH_WAIT_MAX_MS = 10000

/** Shared in-flight refresh so concurrent FETCH actions wait on one refreshLogin. */
let refreshPromise = null

const stripBearer = (token) =>
  token ? String(token).replace(BEARER_PREFIX_RE, "") : ""

const buildAuthHeader = (store) => {
  const token = store.getState().api?.apiToken
  if (!token) return ""
  return token.startsWith("Bearer ") ? token : `Bearer ${token}`
}

const forceLogin = (store) => {
  store.dispatch({ type: types.CLEAR_API_KEY })
  store.dispatch({
    type: types.ERROR,
    data: { redirectUri: "/account/login" }
  })
}

const waitForTokenRefresh = async (store) => {
  if (refreshPromise) {
    try {
      await refreshPromise
    } catch (_) {
      // Refresh failure already forced login; callers will fail on empty auth.
    }
    return
  }
  const started = Date.now()
  while (store.getState().api?.refreshingToken) {
    if (Date.now() - started > REFRESH_WAIT_MAX_MS) {
      break
    }
    await new Promise((resolve) => setTimeout(resolve, REFRESH_WAIT_MS))
  }
}

const performTokenRefresh = async (store) => {
  if (refreshPromise) {
    await waitForTokenRefresh(store)
    return
  }

  store.dispatch({ type: types.REFRESHING_TOKEN })
  refreshPromise = (async () => {
    try {
      const { refreshToken } = store.getState().api
      const tokenRes = await axios.post(`${host}/refreshLogin`, {
        refreshToken
      })
      const nextToken = stripBearer(
        tokenRes.data.apiToken || tokenRes.data.token
      )
      store.dispatch({
        type: types.REFRESHED_TOKEN,
        data: {
          token: nextToken,
          refreshToken: tokenRes.data.refreshToken
        }
      })
    } catch (refreshErr) {
      forceLogin(store)
      throw refreshErr
    } finally {
      refreshPromise = null
    }
  })()

  await refreshPromise
}

const ensureFreshAuthHeader = async (store) => {
  const token = store.getState().api?.apiToken
  const rawToken = stripBearer(token)
  if (!rawToken) {
    return buildAuthHeader(store)
  }

  let decoded
  try {
    decoded = jwtDecode(rawToken)
  } catch (decodeErr) {
    forceLogin(store)
    throw decodeErr
  }

  const issuedAt = decoded.iat
  const expiresAt = decoded.exp
  const validSeconds = expiresAt - issuedAt
  const halfOfTokenExp = 0.5 * validSeconds
  const nowInSeconds = dayjs().valueOf() / 1000
  const needsRefresh = expiresAt - nowInSeconds < halfOfTokenExp

  if (needsRefresh) {
    if (store.getState().api.refreshingToken || refreshPromise) {
      await waitForTokenRefresh(store)
    } else {
      await performTokenRefresh(store)
    }
  }

  return buildAuthHeader(store)
}

export const fetchMiddleware = (rollbar) => (store) => (next) => async (
  action
) => {
  // Guard against undefined or non-standard actions
  if (!action || typeof action.type !== "string") {
    return next(action)
  }

  if (action.type === "FETCH") {
    try {
      let response
      const apiFn = apiCallMap[action.params.type]
      const authHeader = await ensureFreshAuthHeader(store)

      if (action.params.type === "GET") {
        // Filter out undefined/null values from params to prevent empty requests
        const rawParams = action.params.data && action.params.data.params
        
        // For most endpoints, filter out invalid values
        // But be more lenient - only filter truly invalid values
        const cleanParams = rawParams ? Object.fromEntries(
          Object.entries(rawParams)
            .map(([key, value]) => {
              // Special handling for partId - convert to number and ensure it's valid
              if (key === 'partId') {
                const numValue = Number(value)
                if (!isNaN(numValue) && numValue > 0) {
                  return [key, numValue] // Return as number
                }
                return null // Filter out invalid partId
              }
              // Keep the value if it's a valid number (including 0), string, boolean, or array
              if (value === 0 || value === false) return [key, value]
              if (typeof value === 'number' && !isNaN(value) && value > 0) return [key, value]
              if (typeof value === 'string' && value.length > 0) return [key, value]
              if (typeof value === 'boolean') return [key, value]
              if (Array.isArray(value) && value.length > 0) return [key, value]
              // Filter out undefined, null, -1, empty strings, empty arrays
              if (value !== undefined && value !== null && value !== -1 && value !== '') {
                return [key, value]
              }
              return null
            })
            .filter((entry) => entry !== null)
        ) : undefined
        
        // Special check for /userInputs - require partId
        if (action.url === '/userInputs' || action.url === '/v1/userInputs') {
          const partId = rawParams?.partId
          
          // Allow request only if partId is valid (positive number)
          const partIdNum = Number(partId)
          const hasValidPartId = Number.isFinite(partIdNum) && partIdNum > 0
          
          if (!hasValidPartId) {
            // Return empty array instead of making the request (matches backend behavior)
            return { data: [] }
          }
        }
        
        // Only log warning if important params were filtered out (not just -1 or undefined)
        if (rawParams && cleanParams && Object.keys(cleanParams).length < Object.keys(rawParams).length) {
          const filteredKeys = Object.keys(rawParams).filter(key => !(key in cleanParams))
          // Only warn if it's not just -1 or undefined values being filtered
          const importantFiltered = filteredKeys.filter(key => {
            const value = rawParams[key]
            return value !== -1 && value !== undefined && value !== null
          })
          if (importantFiltered.length > 0) {
            console.warn('[apiWrapper] Filtered out invalid params:', importantFiltered)
          }
        }
        
        const requestUrl = `${host}${action.url}`
        const requestConfig = {
          timeout:
            (action.requestMetaData && action.requestMetaData.timeout) ||
            constants.DEFAULT_API_TIMEOUT_MS,
          params: cleanParams,
          headers: { Authorization: authHeader, mode: "cors" }
        }
        
        if (action.url === '/userInputs' || action.url === '/v1/userInputs') {
          console.log('[apiWrapper] Making HTTP request to:', requestUrl, 'with params:', cleanParams, 'config:', requestConfig)
        }
        
        response = await apiFn(requestUrl, requestConfig)
        
        if (action.url === '/userInputs' || action.url === '/v1/userInputs') {
          console.log('[apiWrapper] HTTP request completed for /userInputs, response status:', response?.status, 'data length:', response?.data?.length)
        }
      } else if (
        action.params.type === "POST" ||
        action.params.type === "PUT"
      ) {
        response = await apiFn(
          `${host}${action.url}`,
          action.params.data.requestPayload,
          {
            timeout:
              (action.requestMetaData && action.requestMetaData.timeout) ||
              constants.DEFAULT_API_TIMEOUT_MS,
            headers: {
              ...action.params.data.headers,
              environment: process.env.REACT_APP_ENV,
              Authorization: authHeader || action.params.data.token,
              mode: "cors"
            }
          }
        )
      } else if (action.params.type === "DELETE") {
        response = await apiFn(`${host}${action.url}`, {
          data:
            action.params &&
            action.params.data &&
            action.params.data.requestPayload,
          timeout:
            (action.requestMetaData && action.requestMetaData.timeout) ||
            constants.DEFAULT_API_TIMEOUT_MS,
          headers: { Authorization: authHeader, mode: "cors" }
        })
      }
      return response
    } catch (e) {
      console.log("error in apiWrapper", e)
      if (
        e.toString() ===
        `Error: timeout of ${
          (action.requestMetaData && action.requestMetaData.timeout) ||
          constants.DEFAULT_API_TIMEOUT_MS
        }ms exceeded`
      ) {
        store.dispatch({
          type: types.TOGGLE_MODAL,
          data: {
            redirectUri: "/account/login",
            modal: {
              apiTimeout: true
            }
          }
        })
      }

      if (e.response && statusesToRedirectToLogin.includes(e.response.status)) {
        if (e.response.status === 406) {
          rollbar &&
            rollbar.info(
              `Invalid Refresh Token - ${
                e.response ? JSON.stringify(e.response.data) : e.toString()
              }, id: ${store.getState().user.user.id}`
            )
        }

        store.dispatch({
          type: types.CLEAR_API_KEY
        })

        store.dispatch({
          type: types.ERROR,
          data: {
            redirectUri: "/account/login"
          }
        })
        throw e
      } else {
        rollbar &&
          rollbar.error(
            `Caught in ApiWrapper - ${
              e.response ? JSON.stringify(e.response.data) : e.toString()
            }, id: ${store.getState().user.user.id}`
          )
        throw e
      }
    }
  } else {
    return next(action)
  }
}
